import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import { useCallback, useState } from "react";

import { BPostAddressField } from "@/components/Form/RHF/FormFields/BPostAddressField/bPostAddressField.container";
import { CountryField } from "@/components/Form/RHF/FormFields/CountryField/countryField.container";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { ZipCodeField } from "@/components/Form/RHF/FormFields/ZipCodeField/zipCodeField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { MessageComponent } from "@/components/General/Messages/messages.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { useAddressFormBlockContext } from "../addressFormBlock.context";
import { PaymentAddressFormValues } from "./paymentAddress.schema";

const PaymentAddressPartial = () => {
    const { t } = useGranite();
    const {
        displayAddressFields,
        setDisplayAddressFields,
        disabled,
        addressStatus
    } = useAddressFormBlockContext();

    const [displayTimeout, setDisplayTimeout] = useState(false);

    const handleOnManualEntry = useCallback(() => {
        setDisplayAddressFields(true);
    }, [setDisplayAddressFields]);

    const handleOnTimeout = useCallback(() => setDisplayTimeout(true), []);

    return (
        <>
            <Row>
                <Col md={12}>
                    <CountryField<PaymentAddressFormValues>
                        label={t("general.form.address.country.label")}
                        required
                        name={"payment.country"}
                        onChange={(country: any) => {
                            setDisplayAddressFields(
                                country !== Country.Be ||
                                    addressStatus ===
                                        AddressValidationStatus.OverruledByCustomer
                            );
                        }}
                        disabled={disabled}
                    />
                </Col>
            </Row>
            {!displayAddressFields && (
                <Row>
                    <Col md={12}>
                        {displayTimeout && (
                            <MessageComponent
                                message={{
                                    text: t("bpost.timeout"),
                                    id: "bpost-timeout",
                                    status: "warning",
                                    dismissible: false,
                                    postFix: {
                                        text: t("bpost.timeout.manuelEntry"),
                                        onClick: () => {
                                            setDisplayAddressFields(true);
                                        }
                                    }
                                }}
                            ></MessageComponent>
                        )}
                        <BPostAddressField<PaymentAddressFormValues>
                            test={`customerAddress-input-address`}
                            name={"payment.address"}
                            label={t("general.form.address.addressLine.label")}
                            placeholder={t(
                                "general.form.address.addressLine.placeholder"
                            )}
                            onManualEntry={handleOnManualEntry}
                            onTimeout={handleOnTimeout}
                            disabled={disabled}
                        />
                    </Col>
                </Row>
            )}
            {displayAddressFields && (
                <>
                    <Row>
                        <Col md={6}>
                            <TextField<PaymentAddressFormValues>
                                name={"payment.street"}
                                label={t("general.form.address.street.label")}
                                required
                                placeholder={t(
                                    "general.form.address.street.placeholder"
                                )}
                                autoComplete={"street-address"}
                            />
                        </Col>
                        <Col md={3}>
                            <TextField<PaymentAddressFormValues>
                                name={"payment.number"}
                                label={t("general.form.address.number.label")}
                                required
                                placeholder={t(
                                    "general.form.address.number.placeholder"
                                )}
                                autoComplete={"off"}
                            />
                        </Col>
                        <Col md={3}>
                            <TextField<PaymentAddressFormValues>
                                name={"payment.box"}
                                required={false}
                                label={t("general.form.address.box.label")}
                                placeholder={t(
                                    "general.form.address.box.placeholder"
                                )}
                                autoComplete={"off"}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ZipCodeField<PaymentAddressFormValues>
                                name={"payment.zipCode"}
                                label={t("general.form.address.zipCode.label")}
                                required={true}
                                autoComplete={"postal-code"}
                                countryPath={"payment.country"}
                            />
                        </Col>
                        <Col md={6}>
                            <TextField<PaymentAddressFormValues>
                                name={"payment.city"}
                                label={t("general.form.address.city.label")}
                                required
                                placeholder={t(
                                    "general.form.address.city.placeholder"
                                )}
                                autoComplete={"address-level2"}
                            />
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export { PaymentAddressPartial };
